import React, { useEffect, useState } from "react";
import EmployerUser from "../../../../apiResources/EmployerUser";
import Employer from "../../../../apiResources/Employer";
import SettingsLayout from "../../../layouts/SettingsLayout";
import SettingsHeading from "../../../settings/SettingsHeading";
import Loader from "../../../library/Loader";
import { Table, Thead, Th, Tr, Td } from "../../../library/Table";
import RecruiterUser from "../../../../apiResources/RecruiterUser";
import Recruiter from "../../../../apiResources/Recruiter";
import ResendInvite from "../../../../apiResources/ResendInvite";
import { useTeamMembers } from "../../../../api/company";
import SearchInput from "../../../library/SearchInput";
import SearchBar from "../../../library/SearchBar";
import { useFlash, useModal, usePagination } from "../../../../hooks";
import Pagination from "../../../library/Pagination";
import useQueryParam from "../../../../hooks/useQueryParam";
import InfiniteProgress from "../../../library/InfiniteProgress";
import Button from "../../../library/Button";
import FlashMessage from "../../../shared/general/FlashMessage";
import { useCurrentUser } from "../../../../api/user";
import StatusTag from "../../../StatusTag";
import RemoveTeamMemberModal from "../../../RemoveTeamMemberModal";
import InviteTeamMemberModal from "../../../InviteTeamMemberModal";
import ButtonGroup from "../../../library/ButtonGroup";
import styled from "styled-components";

const Members = (props: Props) => {
  const { user, company } = props;
  const inviteModal = useModal();
  const inviteFlash = useFlash();

  if (!user || !company) {
    return (
      <SettingsLayout user={user} company={company}>
        <Loader />
      </SettingsLayout>
    );
  }

  return (
    <SettingsLayout user={user} company={company}>
      <FlashMessage
        showFlashMessage={inviteFlash.visible}
        success
        message="Invite successfully sent"
      />

      <SettingsHeading>Team Members</SettingsHeading>

      <ButtonGroup spaceBelow align="right">
        <Button secondary onClick={inviteModal.open}>
          Invite Team Member
        </Button>
      </ButtonGroup>

      <InviteTeamMemberModal
        isOpen={inviteModal.isOpen}
        onDismiss={inviteModal.close}
        onSuccess={() => {
          inviteModal.close();
          inviteFlash.show();
        }}
      />

      <TeamMembersTable />
    </SettingsLayout>
  );
};

const TeamMembersTable = () => {
  const [query, setQuery] = useQueryParam<string>("");
  const [memberToRemove, setMemberToRemove] = useState<
    RecruiterUser | EmployerUser
  >(null);
  const pagination = usePagination();
  const removeModal = useModal();
  const successFlash = useFlash();
  const [sentIds, setSentIds] = useState<string[]>([]);

  const { data: user, isLoading: loadingCurrentUser } = useCurrentUser();
  const {
    data,
    isLoading: loadingTeamMembers,
    isIdle,
    isFetching,
  } = useTeamMembers({
    query,
    page: pagination.page,
  });

  useEffect(() => {
    pagination.setPage(1);
  }, [query]);

  const isLoading = loadingCurrentUser || loadingTeamMembers;

  if (isLoading || isIdle) {
    return <Loader />;
  }

  return (
    <div>
      <RemoveTeamMemberModal
        user={memberToRemove}
        isOpen={removeModal.isOpen}
        onDismiss={removeModal.close}
        onSuccess={() => {
          removeModal.close();
          successFlash.show();
          setQuery("");
        }}
      />

      <FlashMessage
        showFlashMessage={successFlash.visible}
        success
        message="User removed from your organisation"
      />

      <SearchBar>
        <SearchInput
          id="team-members-search"
          placeholder="Find a team member"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </SearchBar>
      <InfiniteProgress isLoading={isFetching} />
      <Table id="team-members-table">
        <Thead>
          <Tr>
            <Th colSpan={5}>Members</Th>
          </Tr>
        </Thead>
        <tbody>
          {data.members.length === 0 && (
            <Tr>
              <Td>Sorry, there are no members that match your search</Td>
            </Tr>
          )}
          {data.members.map((member) => (
            <Tr key={member.id}>
              <Td>{member.fullName}</Td>
              <Td>{member.email}</Td>
              <Td>
                {!member.invitationPending && (
                  <StatusTag status="success">Verified</StatusTag>
                )}
                {member.invitationPending &&
                  (sentIds.includes(member.id) ? (
                    <div>Invite sent!</div>
                  ) : (
                    <Button
                      id="resend-invite-button"
                      secondary
                      size="sm"
                      onClick={async () => {
                        const invite = new ResendInvite({ userId: member.id });
                        invite.save();
                        setSentIds((prev) => [...prev, member.id]);
                      }}
                    >
                      <NoWrap>Resend Invite</NoWrap>
                    </Button>
                  ))}
              </Td>
              <Td>{member.role}</Td>
              <Td>
                {member.id === user.id ? (
                  <StatusTag status="info">Your account</StatusTag>
                ) : (
                  <Button
                    id="show-remove-modal-button"
                    primary
                    size="sm"
                    onClick={() => {
                      removeModal.open();
                      setMemberToRemove(member);
                    }}
                  >
                    Remove
                  </Button>
                )}
              </Td>
            </Tr>
          ))}
        </tbody>
      </Table>
      <Pagination pagination={pagination} total={data.meta.total} />
    </div>
  );
};

type Props = {
  user: EmployerUser | RecruiterUser;
  company: Employer | Recruiter;
};

const NoWrap = styled.div`
  white-space: nowrap;
`;

export default Members;
